import React, { useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import "../../../../style/customers.css";
import "../../../../style/journalView.css";

import SaleIcon from "../../../../assets/icons/SaleIcon";
import CreditIcon from "../../../../assets/icons/CreditIcon";
import DebitIcon from "../../../../assets/icons/DebitIcon";
import { useSelector } from "react-redux";

function DailyReportPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasPrintedRef = useRef(false);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const transactions = useMemo(() => location.state?.transactions || [], [location.state?.transactions]);
  const selectedDate = location.state?.selectedDate || null;
  const countMetrics = location.state?.countMetrics || {
    credit: { today: 0, total: 0 },
    debit: { today: 0, total: 0 },
    balance: { today: 0, total: 0 },
  };

  // Helper function to format balance
  const formatBalance = (balance) => {
    if (balance === null || balance === undefined) return "€0.00";
    const amount = parseFloat(balance);
    if (isNaN(amount)) return "€0.00";
    return `€${amount.toFixed(2)}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "";
    }
  };

  // Helper function to format date and time for header
  const formatDateTime = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper function to format selected date for display
  const formatSelectedDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Auto-open print dialog when component loads
  useEffect(() => {
    if (transactions.length > 0 && !hasPrintedRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        window.print();
        hasPrintedRef.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [transactions]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate("/finance/journal");
  };

  // Show loading state if no transactions
  if (!transactions || transactions.length === 0) {
    return (
      <div className="print-invoice-container">
        <div className="print-invoice-header">
          <button className="print-back-btn" onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Journal
          </button>
        </div>
        <div className="loading-container">
          <p>
            No transaction data available. Please export from the Daily Report
            page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="print-invoice-container">
      {/* Header with Print button */}
      <div className="print-invoice-header">
        <div>
          <button className={`${isPartsURL ? "print-back-btn-parts" : isAccessoriesURL ? "print-back-btn-acc" : "print-back-btn"}`} onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Journal
          </button>
        </div>
        <div>
          <button className={`${isPartsURL ? "print-print-btn-parts" : isAccessoriesURL ? "print-print-btn-acc" : "print-print-btn"}`} onClick={handlePrint}>
            <Printer size={16} />
            Print
          </button>
        </div>
      </div>

      {/* Daily Report Content */}
      <div className={`${isPartsURL ? "print-invoice-content-parts" : isAccessoriesURL ? "print-invoice-content-acc" : "print-invoice-content"}`} style={{ maxWidth: "860px" }}>
        {/* Header Section */}
        <div
          className="print-invoice-info"
          style={{
            borderBottom: "1px solid #EAEEF2",
            marginBottom: "20px",
            paddingBottom: "1px",
          }}
        >
          <div
            className="print-invoice-id"
            style={{
              borderBottom: "none",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Daily Report - Transactions
          </div>
          <div
            className="print-invoice-date"
            style={{ fontSize: "14px", fontWeight: "500", color: "#424A53" }}
          >
            {formatDateTime()}
          </div>
        </div>

        {/* Report Date and Summary */}
        <div
          className="print-invoice-info-name"
          style={{ marginBottom: "10px", borderBottom: "none" }}
        >
          {selectedDate && (
            <div
              className="print-customer-name"
              style={{ fontSize: "14px", fontWeight: "400", color: "#424A53" }}
            >
              Report Date:{" "}
              <span style={{ fontWeight: "500" }}>
                {formatSelectedDate(selectedDate)}
              </span>
            </div>
          )}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: "20px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#424A53",
            }}
          >
            <div>
              Total Credit:{" "}
              <span
                className="balance-badge positive"
                style={{ fontWeight: "500" }}
              >
                {formatBalance(countMetrics.credit.today)}
              </span>
            </div>
            <div>
              Total Debit:{" "}
              <span
                className="balance-badge negative"
                style={{ fontWeight: "500" }}
              >
                {formatBalance(countMetrics.debit.today)}
              </span>
            </div>
            <div>
              Balance:{" "}
              <span
                className={`balance-badge ${
                  countMetrics.balance.today >= 0 ? "positive" : "negative"
                }`}
                style={{ fontWeight: "500" }}
              >
                {formatBalance(countMetrics.balance.today)}
              </span>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="customers-table-wrapper">
          <div className="customer-table-responsive">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>TRN ID</th>
                  <th>TYPE</th>
                  <th>DETAILS</th>
                  <th>CREDIT / DEBIT</th>
                  <th>BALANCE</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => {
                  const tnxType =
                    transaction.tnx_type ||
                    transaction.type ||
                    (transaction.is_sales ? "sale" : "credit");

                  return (
                    <tr key={transaction.id}>
                      <td>{formatDate(transaction.date)}</td>
                      <td>{transaction.id || "--"}</td>
                      <td>
                        <div className="customer-details-transaction-type">
                          {(transaction.is_sales || transaction.type === "sale") && (
                            <>
                              <SaleIcon />
                              <span>Sale</span>
                            </>
                          )}
                          {tnxType === "credit" && transaction.type !== "sale" && !transaction.is_sales && (
                            <>
                              <CreditIcon />
                              <span>Credit</span>
                            </>
                          )}
                          {tnxType === "debit" && (
                            <>
                              <DebitIcon />
                              <span>Debit</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {(transaction.is_sales || transaction.type === "sale") ? (
                          <div className="d-flex gap-5 flex-row">
                            <div className="d-flex flex-column">
                              <p
                                className="text-muted"
                                style={{ margin: 0, fontSize: "12px" }}
                              >
                                Bill Amount
                              </p>
                              <div
                                className="customer-details-amount"
                                style={{ fontSize: "14px" }}
                              >
                                €{transaction.bill_amount?.toFixed(2) || "0.00"}
                              </div>
                            </div>
                            <div className="d-flex flex-column">
                              <p
                                className="text-muted"
                                style={{ margin: 0, fontSize: "12px" }}
                              >
                                Paid
                              </p>
                              <div
                                className="customer-details-amount positive"
                                style={{ fontSize: "14px" }}
                              >
                                €{transaction.paid_amount?.toFixed(2) || "0.00"}
                              </div>
                            </div>
                          </div>
                        ) : (
                          transaction.note || "--"
                        )}
                      </td>
                      <td>
                        {(transaction.type === "credit" ||
                          tnxType === "credit" ||
                          transaction.is_sales ||
                          transaction.type === "sale") ? (
                          <div
                            className="customer-details-amount negative"
                            style={{ fontSize: "14px", fontWeight: "500" }}
                          >
                            - €{Math.abs(transaction.amount || transaction.credit || 0).toFixed(2)}
                          </div>
                        ) : (
                          <div
                            className="customer-details-amount positive"
                            style={{ fontSize: "14px", fontWeight: "500" }}
                          >
                            + €{Math.abs(transaction.amount || transaction.debit || 0).toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div
                          className={`balance-badge ${
                            (transaction.balance || 0) >= 0
                              ? "positive"
                              : "negative"
                          }`}
                        >
                          {formatBalance(transaction.balance || 0)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyReportPrint;
